import { IBasicResponse } from 'models/IBasicResponse';
import { emptyFunction, isEmpty } from 'utils';
import { serverResponseError } from 'components/ErrorModal/actions';
import { Dispatch } from 'redux';
import { reduxDispatchEvent } from '../communication';
import { getPage } from 'components/Pagelayout/actions';
import { createHref } from './href';
import warning from 'warning';

type HttpVerb = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

interface IService {
  get: GenericHandlerType;
  post: GenericHandlerType;
  put: GenericHandlerType;
  patch: GenericHandlerType;
  remove: GenericHandlerType;
  appendQueryString: (url: string, query?: unknown) => string;
}

export const Service: IService = {
  get: getJson,
  post: postJson,
  put: putJson,
  patch: patchJson,
  remove: removeJson,
  appendQueryString
};

interface Options {
  headers: Headers;
}

function getHeaders() {
  const headers = new Headers();
  headers.append('X-Requested-With', 'XMLHttpRequest');
  headers.append('Accept', 'application/json; charset=utf-8;');
  headers.append('cache-control', 'no-cache');
  headers.append('Content-Type', 'application/json');
  return headers;
}

const defaultRequest: () => Options = () => ({ headers: getHeaders() });

interface ServerRequest {
  url: string;
  options?: Options;
  cache?: string;
  body?: unknown | string | undefined | null;
}

interface ServerResponse<TResponse> extends IBasicResponse {
  data: TResponse;
}

interface ExtendedRequest extends ServerRequest {
  method: HttpVerb;
}

type GenericHandlerType = <TResponse>(args: ServerRequest) => Promise<TResponse>;
type HandlerType = (args: ServerRequest) => Promise<Response>;

function req({ url, method, options, body }: ExtendedRequest) {
  return fetch(url, {
    ...options,
    method,
    credentials: 'same-origin',
    body: JSON.stringify(body)
  });
}

export function get(args: ServerRequest): Promise<Response> {
  return req({ ...args, method: 'GET' });
}

export function getJson<TResponse>(args: ServerRequest) {
  return jsonHandler<TResponse>(get)(args);
}

export function post(args: ServerRequest): Promise<Response> {
  return req({ ...args, method: 'POST' });
}

export function postJson<TResponse>(args: ServerRequest) {
  return jsonHandler<TResponse>(post)(args);
}

export function put(args: ServerRequest): Promise<Response> {
  return req({ ...args, method: 'PUT' });
}

export function putJson<TResponse>(args: ServerRequest): Promise<TResponse> {
  return jsonHandler<TResponse>(put)(args);
}

export function patch(args: ServerRequest): Promise<Response> {
  return req({ ...args, method: 'PATCH' });
}

export function patchJson<TResponse>(args: ServerRequest): Promise<TResponse> {
  return jsonHandler<TResponse>(patch)(args);
}

export function remove(args: ServerRequest): Promise<Response> {
  return req({ ...args, method: 'DELETE' });
}

export function removeJson<TResponse>(args: ServerRequest): Promise<TResponse> {
  return jsonHandler<TResponse>(remove)(args);
}

function jsonHandler<TResponse>(handler: HandlerType) {
  return (args: ServerRequest) =>
    handler({ options: defaultRequest(), ...args })
      .then<ServerResponse<TResponse>>(responseJson)
      .then(version)
      .then(redirect)
      .then(validate);
}

const validate = <TResponse>(resp: ServerResponse<TResponse>): Promise<TResponse> =>
  new Promise((res, rej) => (!resp.success ? rej(resp) : res(resp.data || ({} as TResponse))));

interface RedirectResponse {
  url: string;
  external: boolean;
}

const HTTP_MOVE_PERM = 301;
const HTTP_FOUND = 302;
const HTTP_SEE_OTHER = 303;
const HTTP_TEMP_REDIRECT = 307;
const HTTP_PERM_REDIRECT = 308;
const isRedirectResponse = <TResponse>(
  resp: ServerResponse<TResponse> | RedirectResponse
): resp is RedirectResponse =>
  [HTTP_MOVE_PERM, HTTP_FOUND, HTTP_SEE_OTHER, HTTP_TEMP_REDIRECT, HTTP_PERM_REDIRECT].includes(
    (resp as ServerResponse<TResponse>).statusCode
  ) && Boolean((resp as RedirectResponse).url);

function redirect<TResponse>(
  resp: ServerResponse<TResponse> | RedirectResponse
): Promise<ServerResponse<TResponse>> {
  // eslint-disable-next-line @typescript-eslint/no-misused-promises
  return new Promise(res => {
    if (!isRedirectResponse(resp)) {
      return res(resp);
    }
    if (resp.external) {
      location.href = resp.url;
      // Returning never resolving promise as locking
      // while we continue with the page redirect
      return new Promise(emptyFunction);
    }
    return reduxDispatchEvent(getPage(createHref(resp.url)));
  });
}

function version<TResponse>(resp: ServerResponse<TResponse>): Promise<ServerResponse<TResponse>> {
  // eslint-disable-next-line @typescript-eslint/no-misused-promises
  return new Promise(res => {
    if ('version' in resp && resp.version !== process.env.BUILD_TAG) {
      warning(resp.version === process.env.BUILD_TAG, 'reloading because versions missmatch');
      location.reload();
      // Returning never resolving promise as locking
      // while we continue with the page reloads
      return new Promise(emptyFunction);
    }
    return res(resp);
  });
}

/**
 * Use in case you want to show an error modal upon getting
 * a server error inside a thunk action
 *
 * Do not pass dispatch as the argument if you need to communicate between
 * apps.
 *
 * TODO: make dispatch required when apps are merged
 *
 * example:
 * ```javascript
 *   Service.get()
 *     .catch(handleServerError(dispatch));
 * ```
 */

const isBasicResponse = (err?: unknown): err is IBasicResponse =>
  (err as IBasicResponse).statusCode !== undefined;
export const handleServerError = (dispatch?: Dispatch) => (err?: IBasicResponse | string) => {
  let messages: string[] = [];

  if (isBasicResponse(err)) {
    messages = err.message;
  } else if (err !== undefined && typeof err === 'string') {
    messages = [err];
  }

  if (Array.isArray(messages) && messages.length > 0) {
    if (dispatch) {
      dispatch(serverResponseError(messages));
    } else {
      void reduxDispatchEvent(serverResponseError(messages));
    }
  }
};

function responseJson<TResponse>(response: Response): Promise<ServerResponse<TResponse>> {
  const contentType = response.headers.get('content-type');
  if (contentType && contentType.indexOf('application/json') === -1) {
    return Promise.reject('server.js: Server response was not json');
  }
  return response.json() as Promise<ServerResponse<TResponse>>;
}

function appendQueryString(url: string, query?: { [key: string]: string | string[] }) {
  if (!query || isEmpty(query)) {
    return url;
  }

  let prefix = /\?/.test(url) ? '&' : '?';
  const [, search] = url.split('?');
  const keys = new Set<string>(search ? search.split('&').map(v => v.split('=')[0]) : []);
  return Object.keys(query).reduce((prev, key) => {
    if (keys.has(key)) {
      return prev;
    }
    const currentQuery = query[key];

    const currentValue = !Array.isArray(currentQuery)
      ? `${key}=${encodeURIComponent(currentQuery)}`
      : currentQuery.map(k => `${key}=${encodeURIComponent(k)}`).join('&');

    if (currentValue === undefined || currentValue.trim().length === 0) {
      return prev;
    }

    const a = `${prev}${prefix}${currentValue}`;
    prefix = '&';
    return a;
  }, url);
}
